import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { planterService, ChurchPlanter, PlanterFilters } from '../api/planterService';
import { userService } from '../api/userService';
import { hubService, Hub } from '../api/hubService';

const ChurchPlanters: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hubFilter, setHubFilter] = useState('');
  const [geoFilter, setGeoFilter] = useState('');
  const [selectedPlanter, setSelectedPlanter] = useState<ChurchPlanter | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGeocodeModal, setShowGeocodeModal] = useState(false);
  const [geocodeResults, setGeocodeResults] = useState<any>(null);

  const filters: PlanterFilters = {
    page,
    limit: limit === 0 ? 10000 : limit, // 0 means "all", send a very high number
    search: search || undefined,
    status: statusFilter || undefined,
    assignedHub: hubFilter || undefined,
  };

  // Fetch planters
  const { data: plantersData, isLoading } = useQuery({
    queryKey: ['churchPlanters', filters],
    queryFn: () => planterService.getAllPlanters(filters),
  });

  // Fetch hub leaders
  const { data: hubLeadersData } = useQuery({
    queryKey: ['hubLeaders'],
    queryFn: () => userService.getUsersByRole('hub_leader'),
  });

  // Fetch hubs
  const { data: hubsData } = useQuery({
    queryKey: ['hubs'],
    queryFn: () => hubService.getAllHubs({ limit: 100 }),
  });

  const hubLeaders = hubLeadersData?.data || [];
  const availableHubs = hubsData?.data || [];

  // Update planter mutation
  const updatePlanterMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      planterService.updatePlanter(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['churchPlanters'] });
      queryClient.invalidateQueries({ queryKey: ['admin-hub-contact-overview'] });
      setShowEditModal(false);
      setSelectedPlanter(null);
    },
  });

  // Bulk geocode mutation
  const bulkGeocodeMutation = useMutation({
    mutationFn: () => planterService.bulkGeocodePlanters(),
    onSuccess: (data) => {
      setGeocodeResults(data.data);
      queryClient.invalidateQueries({ queryKey: ['churchPlanters'] });
    },
  });

  let planters: ChurchPlanter[] = plantersData?.data || [];
  const pagination = plantersData?.pagination;

  // Apply client-side geo filter if set
  if (geoFilter === 'missing') {
    planters = planters.filter(planter => !planter.latitude || !planter.longitude);
  } else if (geoFilter === 'complete') {
    planters = planters.filter(planter => planter.latitude && planter.longitude);
  }

  // Calculate hub statistics with hub leader info
  const hubStats = planters.reduce((acc: any, planter: ChurchPlanter) => {
    const hubName = planter.assignedHub || 'Unassigned';
    if (!acc[hubName]) {
      const hub = availableHubs.find((h: Hub) => h.name === hubName);
      acc[hubName] = { 
        total: 0,
        hubLeader: hub?.hubLeader || null
      };
    }
    acc[hubName].total++;
    return acc;
  }, {});

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      prospect: 'bg-gray-100 text-gray-800',
      candidate: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      graduated: 'bg-purple-100 text-purple-800',
      inactive: 'bg-red-100 text-red-800',
      withdrawn: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Church Planters</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage church planters, hub assignments, and coaching relationships
          </p>
        </div>

        {/* Hub Statistics */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Hub Overview</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(hubStats).map(([hubName, stats]: [string, any]) => (
              <div key={hubName} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {hubName}
                          {stats.hubLeader && (
                            <span className="text-xs text-gray-400 block mt-1">
                              {stats.hubLeader.firstName} {stats.hubLeader.lastName}
                            </span>
                          )}
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.total}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="limit" className="text-sm font-medium text-gray-700">
                  Show:
                </label>
                <select
                  id="limit"
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setPage(1); // Reset to first page when changing limit
                  }}
                  className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={0}>All</option>
                </select>
                <span className="text-sm text-gray-600">planters per page</span>
              </div>
              {pagination && (
                <div className="text-sm text-gray-600">
                  Showing {planters.length} of {pagination.total} planters
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-4">
              <div>
                <input
                  type="text"
                  placeholder="Search planters..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Status</option>
                  <option value="prospect">Prospect</option>
                  <option value="candidate">Candidate</option>
                  <option value="active">Active</option>
                  <option value="graduated">Graduated</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <select
                  value={hubFilter}
                  onChange={(e) => setHubFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Hubs</option>
                  {availableHubs.map((hub: Hub) => (
                    <option key={hub.id} value={hub.name}>
                      {hub.name}
                    </option>
                  ))}
                  <option value="unassigned">Unassigned</option>
                </select>
              </div>
              <div>
                <select
                  value={geoFilter}
                  onChange={(e) => setGeoFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Geo Data</option>
                  <option value="missing">Missing Geo Data</option>
                  <option value="complete">Has Geo Data</option>
                </select>
              </div>
              <div>
                <button
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setHubFilter('');
                    setGeoFilter('');
                    setPage(1);
                  }}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reset Filters
                </button>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setGeocodeResults(null);
                  setShowGeocodeModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Geocode All Planters
              </button>
            </div>
          </div>
        </div>

        {/* Planters Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="sm:hidden divide-y divide-gray-200">
            {isLoading ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">Loading...</div>
            ) : planters.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-gray-500">No church planters found</div>
            ) : (
              planters.map((planter: ChurchPlanter) => (
                <div key={planter.id} className="px-4 py-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{planter.name}</p>
                    {planter.spouseName && <p className="text-xs text-gray-500">& {planter.spouseName}</p>}
                  </div>
                  <p className="text-sm text-gray-700">Church: {planter.churchName || 'N/A'}</p>
                  <p className="text-sm text-gray-700">
                    City: {planter.city ? `${planter.city}${planter.state ? `, ${planter.state}` : ''}` : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-700">
                    Hub Leader:{' '}
                    {planter.hubLeader
                      ? `${planter.hubLeader.firstName} ${planter.hubLeader.lastName}`
                      : 'Unassigned'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {planter.latitude && planter.longitude ? 'Geocoded' : 'Missing geo'}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedPlanter(planter);
                      setShowEditModal(true);
                    }}
                    className="text-sm font-medium text-primary-600 hover:text-primary-900"
                  >
                    Edit
                  </button>
                </div>
              ))
            )}
          </div>
          <ResponsiveTableWrapper className="hidden sm:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Planter
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Church
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    City / Geo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Hub Leader
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                      Loading...
                    </td>
                  </tr>
                ) : planters.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                      No church planters found
                    </td>
                  </tr>
                ) : (
                  planters.map((planter: ChurchPlanter) => (
                    <tr key={planter.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-700 font-medium text-sm">
                                {planter.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .substring(0, 2)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{planter.name}</div>
                            {planter.spouseName && (
                              <div className="text-sm text-gray-500">& {planter.spouseName}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-normal text-sm text-gray-900 sm:px-6 sm:whitespace-nowrap">
                        {planter.churchName || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                        <div className="text-sm text-gray-900">
                          {planter.city ? `${planter.city}${planter.state ? `, ${planter.state}` : ''}` : 'N/A'}
                        </div>
                        {planter.latitude && planter.longitude ? (
                          <div className="text-xs text-green-600 flex items-center mt-1">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Geocoded
                          </div>
                        ) : (
                          <div className="text-xs text-red-600 flex items-center mt-1">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Missing
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sm:px-6">
                        {planter.hubLeader ? (
                          `${planter.hubLeader.firstName} ${planter.hubLeader.lastName}`
                        ) : (
                          <span className="text-gray-500">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                        <button
                          onClick={() => {
                            setSelectedPlanter(planter);
                            setShowEditModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ResponsiveTableWrapper>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && limit !== 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{' '}
                    <span className="font-medium">{pagination.pages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                      disabled={page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Edit Planter Modal */}
        {showEditModal && selectedPlanter && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowEditModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const hubLeaderId = formData.get('assignedHubLeader') as string;
                    const hubName = formData.get('assignedHub') as string;
                    const hubContactTrackingExempt = formData.get('hubContactTrackingExempt') === 'on';
                    updatePlanterMutation.mutate({
                      id: selectedPlanter.id,
                      data: {
                        name: formData.get('name') as string,
                        spouseName: formData.get('spouseName') as string,
                        churchName: formData.get('churchName') as string,
                        city: formData.get('city') as string,
                        state: formData.get('state') as string,
                        status: formData.get('status') as string,
                        stage: formData.get('stage') as string,
                        assignedHub: hubName || null,
                        assignedHubLeader: hubLeaderId ? parseInt(hubLeaderId) : null,
                        hubContactTrackingExempt,
                      },
                    });
                  }}
                >
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Edit Church Planter
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          name="name"
                          defaultValue={selectedPlanter.name}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Spouse Name
                        </label>
                        <input
                          type="text"
                          name="spouseName"
                          defaultValue={selectedPlanter.spouseName || ''}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Church Name
                        </label>
                        <input
                          type="text"
                          name="churchName"
                          defaultValue={selectedPlanter.churchName || ''}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            defaultValue={selectedPlanter.city || ''}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            State
                          </label>
                          <input
                            type="text"
                            name="state"
                            defaultValue={selectedPlanter.state || ''}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                          name="status"
                          defaultValue={selectedPlanter.status}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="prospect">Prospect</option>
                          <option value="candidate">Candidate</option>
                          <option value="active">Active</option>
                          <option value="graduated">Graduated</option>
                          <option value="inactive">Inactive</option>
                          <option value="withdrawn">Withdrawn</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hub</label>
                        <select
                          name="assignedHub"
                          defaultValue={selectedPlanter.assignedHub || ''}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="">Unassigned</option>
                          {availableHubs.map((hub: Hub) => (
                            <option key={hub.id} value={hub.name}>
                              {hub.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hub Leader</label>
                        <select
                          name="assignedHubLeader"
                          defaultValue={selectedPlanter.assignedHubLeader || ''}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="">Unassigned</option>
                          {hubLeaders.map((leader) => (
                            <option key={leader.id} value={leader.id}>
                              {leader.firstName} {leader.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="hubContactTrackingExempt"
                            defaultChecked={Boolean(selectedPlanter.hubContactTrackingExempt)}
                            className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">
                              Exclude from hub contact tracking
                            </span>
                            <span className="block text-xs text-gray-600 mt-0.5">
                              When enabled, this planter will not appear as needing contact for hub leader cadence
                              (for planters not in the normal hub leader contact system).
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedPlanter(null);
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Geocode Modal */}
        {showGeocodeModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !bulkGeocodeMutation.isPending && setShowGeocodeModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Bulk Geocode Church Planters
                  </h3>
                  
                  {!bulkGeocodeMutation.isPending && !geocodeResults && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-4">
                        This will geocode all church planters that don't have location coordinates yet. 
                        The process uses their city, state, and zip code to determine latitude and longitude.
                      </p>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> This process respects rate limits (1 request per second) and may take several minutes if you have many planters.
                        </p>
                      </div>
                    </div>
                  )}

                  {bulkGeocodeMutation.isPending && (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                      <p className="text-sm text-gray-600">
                        Geocoding planters... This may take a few minutes.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Please do not close this window.
                      </p>
                    </div>
                  )}

                  {geocodeResults && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <h4 className="text-sm font-medium text-green-800 mb-2">Geocoding Complete!</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Total Planters:</span>
                            <span className="ml-2 font-medium text-gray-900">{geocodeResults.total}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Processed:</span>
                            <span className="ml-2 font-medium text-gray-900">{geocodeResults.processed}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Succeeded:</span>
                            <span className="ml-2 font-medium text-green-600">{geocodeResults.succeeded}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Failed:</span>
                            <span className="ml-2 font-medium text-red-600">{geocodeResults.failed}</span>
                          </div>
                        </div>
                      </div>

                      {geocodeResults.results && geocodeResults.results.length > 0 && (
                        <div className="max-h-96 overflow-y-auto">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Results:</h4>
                          <div className="space-y-2">
                            {geocodeResults.results.map((result: any, index: number) => (
                              <div
                                key={index}
                                className={`text-sm p-3 rounded-md ${
                                  result.status === 'success'
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                }`}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-900">{result.name}</span>
                                    {result.status === 'success' ? (
                                      <div className="text-xs text-gray-600 mt-1">
                                        Coordinates: {result.latitude?.toFixed(4)}, {result.longitude?.toFixed(4)}
                                        {result.assignedHub && (
                                          <span className="ml-2 text-primary-600">→ {result.assignedHub}</span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-red-600 mt-1">
                                        {result.reason || 'Failed to geocode'}
                                      </div>
                                    )}
                                  </div>
                                  <span
                                    className={`ml-2 px-2 py-1 text-xs rounded ${
                                      result.status === 'success'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {result.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {bulkGeocodeMutation.isError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-sm text-red-800">
                        <strong>Error:</strong> Failed to start geocoding process. Please try again.
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  {!bulkGeocodeMutation.isPending && !geocodeResults && (
                    <>
                      <button
                        type="button"
                        onClick={() => bulkGeocodeMutation.mutate()}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Start Geocoding
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowGeocodeModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  
                  {geocodeResults && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowGeocodeModal(false);
                        setGeocodeResults(null);
                      }}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChurchPlanters;
